<template>
    <div
        v-if="todisplay"
        v-show="isvisible"
        :class="rootClasses"
        :style="rootStyles"
    >
        <!-- Hidden background services (stay mounted so Root.ChildControlByName keeps working).
             Lift parking when any HSM is visible for Debug → Show HSMs. -->
        <div
            v-if="menusReady"
            class="cch-bg-services"
            :class="{ 'cch-bg-services--debug': hsmDebugVisible }"
            :aria-hidden="hsmDebugVisible ? 'false' : 'true'"
        >
            <DynamicControlContainer
                v-for="(svc, si) in backgroundServices"
                :key="svcKey(svc, si)"
                :name="'bgService_' + si"
                :root="effectiveRoot"
                type="DynamicControlContainer"
                parent-type="CallCenterHome"
                :control-data="bgServiceControlData(svc)"
                :source-data="svc"
                :scopeitems="controlBag"
                :controlscope="controlscope"
                @finished-render="onBgServiceFinished"
            />
        </div>

        <BadgeHandler
            v-if="menusReady"
            name="badgeHandler"
            :root="effectiveRoot"
            type="BadgeHandler"
            parent-type="CallCenterHome"
            :control-data="badgeHandlerControlData"
            :scopeitems="controlBag"
            :controlscope="controlscope"
            @finished-render="finishRenderHandler"
        />

        <template v-if="chromeReady">
            <!-- Title bar -->
            <div class="cch-titlebar" :style="{ backgroundColor: bannerColor }">
                <QIcon
                    v-if="GlobalVars.api_busy_indicator"
                    name="mdi-cloud-arrow-down"
                    color="white"
                    size="24px"
                    class="cch-busy"
                >
                    <QTooltip>{{ Translate('Receiving Data...') }}</QTooltip>
                </QIcon>

                <img
                    v-if="companyLogoSrc"
                    class="cch-logo"
                    :src="companyLogoSrc"
                    alt=""
                />

                <div class="cch-customer text-white">
                    {{ customerName }}
                </div>

                <div v-if="System.DEBUG" class="cch-debug text-white text-caption q-ml-md">
                    ({{ bandIdLabel }})
                </div>

                <QBtn
                    v-if="System.DEBUG"
                    flat
                    dense
                    round
                    color="white"
                    icon="mdi-menu-down"
                    class="cch-debug-menu-btn"
                    :aria-label="Translate('View more options')"
                >
                    <QTooltip anchor="center end" self="center left" transition-show="scale" transition-hide="scale">{{ Translate('View more options') }}</QTooltip>
                    <QMenu anchor="bottom left" self="top left">
                        <QList dense class="cch-debug-menu text-no-wrap">
                            <QItem
                                v-for="item in debugMenuItems"
                                :key="item.id"
                                v-close-popup
                                clickable
                                @click="item.run"
                            >
                                <QItemSection>{{ item.label }}</QItemSection>
                            </QItem>
                        </QList>
                    </QMenu>
                </QBtn>

                <div class="cch-tabs-grow">
                    <QTabs
                        v-if="vars.DynamicTabArray.length > 0"
                        dense
                        dense-indicator
                        outside-arrows
                        mobile-arrows
                        class="cch-dyn-tabs"
                        active-color="white"
                        indicator-color="white"
                        :model-value="vars.ActiveDynamicTab"
                        @update:model-value="selectDynamic"
                    >
                        <QTab
                            v-for="(tab, ti) in vars.DynamicTabArray"
                            :key="tab.ID || ti"
                            :name="ti"
                            class="cch-dyn-tab"
                            @mousedown.middle.prevent
                            @auxclick.middle.prevent="closeDynamic(ti)"
                        >
                            <div class="cch-dyn-tab-inner">
                                <span class="cch-dyn-tab-label">{{ tab.Title }}</span>
                                <QIcon
                                    name="mdi-close-circle"
                                    size="xs"
                                    class="cch-dyn-tab-close"
                                    @click.stop="closeDynamic(ti)"
                                />
                            </div>
                            <QTooltip v-if="tab.Tooltip">{{ tab.Tooltip }}</QTooltip>
                        </QTab>
                    </QTabs>
                </div>

                <div class="cch-alert-area">
                    <DynamicControlContainer
                        v-for="(ctrl, ai) in alertAreaControls"
                        :key="'alert_' + ai"
                        :name="'alertArea_' + ai"
                        :root="effectiveRoot"
                        type="DynamicControlContainer"
                        parent-type="CallCenterHome"
                        :control-data="wrapAlertAreaContent(ctrl)"
                        :source-data="ctrl"
                        :scopeitems="controlBag"
                        :controlscope="controlscope"
                        @finished-render="finishRenderHandler"
                    />
                </div>

                <div class="cch-title-right">
                    <DynamicControlContainer
                        v-for="(ctrl, ri) in titleRightControls"
                        :key="'tright_' + ri"
                        :name="'titleRight_' + ri"
                        :root="effectiveRoot"
                        type="DynamicControlContainer"
                        parent-type="CallCenterHome"
                        :control-data="wrapTitleRightContent(ctrl)"
                        :source-data="ctrl"
                        :scopeitems="controlBag"
                        :controlscope="controlscope"
                        @finished-render="finishRenderHandler"
                    />
                </div>
            </div>

            <!-- Main row: nav + content -->
            <div class="cch-main">
                <div
                    class="cch-nav"
                    :class="{ 'cch-nav--dynamic-active': showDynamicLayer }"
                    :style="navWrapperStyle"
                >
                    <IconDrawer
                        :items="iconDrawerItems"
                        :model-value="vars.ActiveStaticTab"
                        :width="navWidth"
                        :expanded="!navCollapsed"
                        dark
                        background-color="navigation"
                        :root="effectiveRoot"
                        :scopeitems="controlBag"
                        :controlscope="controlscope"
                        name-prefix="cchNav"
                        @update:model-value="onNavSelect"
                        @select="onNavSelect"
                        @menu-action="onNavMenuAction"
                        @action="onNavAction"
                        @item-finished-render="finishRenderHandler"
                    />
                </div>

                <div class="cch-content">
                    <!-- Static panels (visited, kept mounted) -->
                    <div
                        v-for="idx in visitedStaticTabs"
                        v-show="showStaticLayer && vars.ActiveStaticTab === idx"
                        :key="'static_' + idx"
                        class="cch-panel"
                    >
                        <!--
                          Vue2 CallCenterHome5 parity: staticTabContent mounts parent MenuItemData.ControlData
                          for every static tab; VisibleExpression hides it for MenuName/URL while PostrenderActions
                          and Javascript still run (DynamicControlContainer → HiddenContainer).
                        -->
                        <DynamicControlContainer
                            v-if="needsHiddenStaticTabHost(idx)"
                            :name="'staticTabContent_' + idx"
                            :root="effectiveRoot"
                            type="DynamicControlContainer"
                            parent-type="ControlContainer"
                            :control-data="staticTabHostControlData(idx)"
                            :source-data="menuModel[idx] && menuModel[idx].Data"
                            :scopeitems="controlBag"
                            :controlscope="controlscope"
                            @finished-render="finishRenderHandler"
                        />

                        <!-- Direct ControlData (None + StaticTab) -->
                        <div v-if="isNoneStaticTab(idx)" class="cch-fill-host">
                            <DynamicControlContainer
                                :name="'staticContent_' + idx"
                                :root="effectiveRoot"
                                type="DynamicControlContainer"
                                parent-type="ControlContainer"
                                :control-data="staticNoneControlData(idx)"
                                :source-data="menuModel[idx] && menuModel[idx].Data"
                                :scopeitems="controlBag"
                                :controlscope="controlscope"
                                @finished-render="finishRenderHandler"
                            />
                        </div>

                        <!-- MenuName / URL secondary menu -->
                        <div
                            v-else-if="isMenuStaticTab(idx)"
                            class="cch-menu-layout"
                        >
                            <div class="cch-menu-list-host">
                                <DynamicMenuList
                                    :ref="(el) => setMenuListRef(idx, el)"
                                    :name="'menuList_' + idx"
                                    :root="effectiveRoot"
                                    type="DynamicMenuList"
                                    parent-type="HorizontalLayout"
                                    :control-data="menuListControlData(idx)"
                                    :source-data="menuModel[idx] && menuModel[idx].Data"
                                    :scopeitems="controlBag"
                                    :controlscope="controlscope"
                                    @finished-render="(c) => onMenuListReady(idx, c)"
                                />
                            </div>

                            <div class="cch-menu-content">
                                <!-- Per-item panels (absolute fill hosts so grids get a definite height) -->
                                <div
                                    v-for="itemKey in visitedItemKeys(idx)"
                                    v-show="isActiveMenuContent(idx, 'item', itemKey)"
                                    :key="'item_' + idx + '_' + itemKey"
                                    class="cch-fill-host"
                                >
                                    <DynamicControlContainer
                                        :name="'menuItem_' + idx + '_' + itemKey"
                                        :root="effectiveRoot"
                                        type="DynamicControlContainer"
                                        parent-type="ControlContainer"
                                        :control-data="itemPanelControlData(idx, itemKey)"
                                        :source-data="menuItemEntry(idx, itemKey)"
                                        :scopeitems="controlBag"
                                        :controlscope="controlscope"
                                        @finished-render="finishRenderHandler"
                                    />
                                </div>

                                <!-- Shared panels (name-keyed singletons per static tab) -->
                                <div
                                    v-for="panelName in visitedSharedKeys(idx)"
                                    v-show="isActiveMenuContent(idx, 'shared', panelName)"
                                    :key="'shared_' + idx + '_' + panelName"
                                    class="cch-fill-host"
                                >
                                    <DynamicControlContainer
                                        :name="'shared_' + idx + '_' + panelName"
                                        :root="effectiveRoot"
                                        type="DynamicControlContainer"
                                        parent-type="ControlContainer"
                                        :control-data="sharedPanelControlData(panelName)"
                                        :source-data="sharedPanelDoc(panelName)"
                                        :scopeitems="controlBag"
                                        :controlscope="controlscope"
                                        @finished-render="finishRenderHandler"
                                    />
                                </div>

                                <div
                                    v-if="missingSharedMessage(idx)"
                                    class="cch-missing-shared text-negative q-pa-md"
                                >
                                    {{ missingSharedMessage(idx) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Dynamic tab layer (sibling; shown when a dynamic tab is active) -->
                    <div
                        v-for="(tab, ti) in vars.DynamicTabArray"
                        v-show="showDynamicLayer && vars.ActiveDynamicTab === ti"
                        :key="'dyn_' + (tab.ID || ti)"
                        class="cch-panel cch-dyn-panel"
                    >
                        <div v-if="tab.ControlData" class="cch-fill-host">
                            <DynamicControlContainer
                                :name="'dynContent_' + (tab.ID || ti)"
                                :root="effectiveRoot"
                                type="DynamicControlContainer"
                                parent-type="ControlContainer"
                                :control-data="dynamicTabControlData(tab, ti)"
                                :source-data="tab"
                                :scopeitems="controlBag"
                                :controlscope="controlscope"
                                @finished-render="(c) => onDynamicTabFinishedRender(tab, c)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { nextTick } from 'vue';
import { QIcon, QTabs, QTab, QTooltip, QBtn, QMenu, QList, QItem, QItemSection, ClosePopup } from 'quasar';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';
import api from '@/Services/api';
import EventBus from '../event-bus.js';
import { appSettings } from '@/Shared/appSettings.js';
import careService from '@/Services/careService.jsx';
import IconDrawer from './vuecontrols/IconDrawer.vue';
import { buildNavigationDrawerIconItems } from './vuecontrols/navigationDrawerIconItems.js';
import DynamicControlContainer from './DynamicControlContainer.jsx';
import DynamicMenuList from './DynamicMenuList.jsx';
import BadgeHandler from './BadgeHandler.jsx';
import {
    buildCareCenterMenu,
    getBannerColor,
    parseTabTitle,
    normalizeOpenTab,
    resolveMenuContent,
    ensureSharedPanelHostControlData,
} from './CallCenterHome/menuModel.js';

/** Cap visit stack so long sessions do not grow without bound. */
const NAV_HISTORY_MAX = 50;

const DEFAULT_VARS = {
    ActiveStaticTab: 0,
    ActiveDynamicTab: -1,
    DynamicTabArray: [],
    // Filled by loadCustomer() (Home5c used a Widgets, Inc. placeholder until the GET returned)
    Customer: { Name: '' },
    bgServicesReady: false,
    Locale: 'en',
};

export default {
    name: 'CallCenterHome',
    components: {
        QIcon,
        QTabs,
        QTab,
        QTooltip,
        QBtn,
        QMenu,
        QList,
        QItem,
        QItemSection,
        IconDrawer,
        DynamicControlContainer,
        DynamicMenuList,
        BadgeHandler,
    },
    directives: {
        ClosePopup,
    },
    mixins: [BaseComponentMixin],
    props: {},
    data() {
        return {
            menusReady: false,
            bgServicesSettled: false,
            bgServicesPending: 0,
            hsmDebugVisible: false,
            navCollapsed: true,
            canAddCurrentUserToCustomer: false,
            menuModel: [],
            visitedStaticTabs: [],
            // { [staticIndex]: string[] }
            visitedItemsByStatic: {},
            // { [staticIndex]: string[] }
            visitedSharedByStatic: {},
            // { [staticIndex]: { kind, key, message? } }
            activeMenuContentByStatic: {},
            menuListRefs: {},
            menuListWatchers: {},
            missingSharedLogged: {},
            /**
             * Visit stack for close-tab restore.
             * Shape: one static/menu root, then dynamic tab hops.
             * Visiting a menu/static location clears prior history (menus are not closable).
             * Entries: { type:'static', staticIndex, menuSelectedId? } | { type:'dynamic', tabId }
             */
            navHistory: [],
            /** Skip history pushes while restoreNavEntry is applying a prior location. */
            _suppressNavHistory: false,
            /** After closing the active dynamic tab, restore another dynamic without static deactivate. */
            _skipStaticDeactivateOnDynamicSelect: false,
            /** Tab IDs whose DynamicControlContainer has finished first render (Vue2 renderedTabs). */
            renderedDynamicTabs: new Set(),
            // Used when parent does not pass a Control (scopeitems) bag
            ownedScopeitems: {},
            openedEditorFromQuery: false,
        };
    },
    computed: {
        /** Shared Control bag for nested Params (Control.SelectedMenuName, etc.). */
        controlBag() {
            if (this.scopeitems && typeof this.scopeitems === 'object')
                return this.scopeitems;
            return this.ownedScopeitems;
        },
        rootClasses() {
            return {
                'c-CallCenterHome': true,
                [`c-name-${this.name || 'mainContainer'}`]: true,
            };
        },
        rootStyles() {
            return {
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                ...utils.resolveStyleHints(this.styleHints, this),
                ...this.sizeStyle,
            };
        },
        effectiveRoot() {
            return this.root || this;
        },
        chromeReady() {
            return this.menusReady && this.vars.bgServicesReady;
        },
        showDynamicLayer() {
            return this.vars.ActiveDynamicTab >= 0;
        },
        showStaticLayer() {
            return !this.showDynamicLayer;
        },
        bannerColor() {
            return getBannerColor(this.System);
        },
        companyLogoSrc() {
            const wl = this.System?.WhiteLabel;
            if (!wl?.CompanyLogoString)
                return null;
            return `data:${wl.CompanyLogoType || 'image/png'};base64,${wl.CompanyLogoString}`;
        },
        customerName() {
            return this.vars?.Customer?.Name || '';
        },
        /** Home5c titleBar: ({{ System.careTokenService.BandID() }}) when DEBUG — not Environment. */
        bandIdLabel() {
            const band = this.System?.BandID
                ?? this.System?.careTokenService?.BandID?.()
                ?? '';
            return band || '';
        },
        currentUserId() {
            return this.System?.careTokenService?.UserID?.()
                ?? this.System?.UserID
                ?? '';
        },
        currentCustomerId() {
            return this.vars?.Customer?.CustomerID
                ?? this.vars?.Customer?.Id
                ?? '';
        },
        /** Home5c titleBar PopupMenuButton (DisplayExpression: System.DEBUG). */
        debugMenuItems() {
            const customerId = this.currentCustomerId;
            const userId = this.currentUserId;
            const items = [
                {
                    id: 'copy-customer',
                    label: `Copy Customer Id ${customerId}`.trim(),
                    run: () => this.copyDebugId(customerId),
                },
                {
                    id: 'copy-user',
                    label: `Copy Current User Id ${userId}`.trim(),
                    run: () => this.copyDebugId(userId),
                },
                {
                    id: 'duplicate-tab',
                    label: 'Duplicate Browser Tab',
                    run: () => this.duplicateBrowserTab(),
                },
            ];

            if (this.canAddCurrentUserToCustomer) {
                items.push({
                    id: 'add-user-company',
                    label: 'Add Current User to Company',
                    run: () => this.addCurrentUserToCompany(),
                });
            }

            return items;
        },
        navWidth() {
            return this.navCollapsed ? 56 : 256;
        },
        navWrapperStyle() {
            const w = this.navWidth;
            return {
                width: `${w}px`,
                minWidth: `${w}px`,
                maxWidth: `${w}px`,
                flexShrink: 0,
                overflow: 'hidden',
                transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease',
                zIndex: 2000,
                height: '100%',
            };
        },
        iconDrawerItems() {
            return buildNavigationDrawerIconItems({
                model: this.menuModel,
                collapsed: this.navCollapsed,
                translate: (k) => this.Translate(k),
                generateUUID: () => utils.generateUUID(),
                onCollapseToggle: () => { this.navCollapsed = !this.navCollapsed; },
            });
        },
        backgroundServices() {
            return utils.document_cache.menus?.CareCenterBackgroundServices || [];
        },
        alertAreaControls() {
            const areas = utils.document_cache.areas?.MainBodyAlertNotification;
            if (!Array.isArray(areas))
                return [];
            return areas.flatMap((a) => a.Controls || []);
        },
        titleRightControls() {
            const areas = utils.document_cache.areas?.TitleBarRightSide;
            if (!Array.isArray(areas))
                return [];
            return areas.flatMap((a) => a.Controls || []);
        },
        badgeHandlerControlData() {
            return {
                SourceType: 'Raw',
                SourceAsMenuItems: true,
                SourceRaw: '{# utils.document_cache.menus.CareCenterMenu.map(m => ({ ...m, ChildMenus: m.MenuItemData.ChildMenuName ? utils.document_cache.menus[m.MenuItemData.ChildMenuName] : null })) #}',
            };
        },
        totalItems() {
            return 1;
        },
    },
    watch: {
        chromeReady(ready) {
            if (ready)
                this.tryOpenEditorFromQuery();
        },
    },
    beforeCreate() {
        // Runs before mixin created/preRenderComplete so EventBus is ready for bootstrap overlay.
        this._cchBoundActionTab = (a) => this.onActionTab?.(a);
        this._cchBoundRenameTab = (a) => this.onActionRenameTab?.(a);
        this._cchBoundClose = (a) => this.onActionClose?.(a);
        this._cchBoundSelectMenu = (a) => this.onActionSelectMenuItem?.(a);
        this._cchBoundOpenEditor = (a) => this.onOpenDocumentEditor?.(a);
        this._cchBoundHsmVisibility = () => this.refreshHsmDebugVisible();
    },
    created() {
        EventBus.$on('Action-Tab', this._cchBoundActionTab);
        EventBus.$on('Action-RenameTab', this._cchBoundRenameTab);
        EventBus.$on('Action-SelectMenuItem', this._cchBoundSelectMenu);
        EventBus.$on('OpenDocumentEditor', this._cchBoundOpenEditor);
        EventBus.$on('HSM-VisibilityChanged', this._cchBoundHsmVisibility);
        // Close is Parent-broadcast; listen on the control event bus (not EventBus)
        this.$on('Action-Close', this._cchBoundClose);
    },
    unmounted() {
        EventBus.$off('Action-Tab', this._cchBoundActionTab);
        EventBus.$off('Action-RenameTab', this._cchBoundRenameTab);
        EventBus.$off('Action-SelectMenuItem', this._cchBoundSelectMenu);
        EventBus.$off('OpenDocumentEditor', this._cchBoundOpenEditor);
        EventBus.$off('HSM-VisibilityChanged', this._cchBoundHsmVisibility);
        this.$off('Action-Close', this._cchBoundClose);

        Object.values(this.menuListWatchers).forEach((stop) => {
            if (typeof stop === 'function')
                stop();
        });
        this.menuListWatchers = {};

        if (utils.global_control_lookup.mainContainer === this)
            delete utils.global_control_lookup.mainContainer;
    },
    methods: {
        getSizeDefaults() {
            return {
                Width: { Mode: 'Fill' },
                Height: { Mode: 'Fill' },
            };
        },

        ensureShellState() {
            if (!this.controlData)
                this.controlData = {};
            if (!this.controlData.Name)
                this.controlData.Name = 'mainContainer';

            this.vars = {
                ...DEFAULT_VARS,
                ...(this.vars || {}),
                DynamicTabArray: Array.isArray(this.vars?.DynamicTabArray) ? this.vars.DynamicTabArray : [],
                Customer: this.vars?.Customer || { Name: '' },
            };

            const bag = this.controlBag;
            if (bag.SelectedMenuName == null)
                bag.SelectedMenuName = '';
            if (bag.SelectedMenuTitle == null)
                bag.SelectedMenuTitle = '';

            utils.global_control_lookup.mainContainer = this;
            utils.global_control_lookup[this.controlData.Name] = this;
        },

        writeSelectedMenuFields(name, title) {
            const bag = this.controlBag;
            bag.SelectedMenuName = name || '';
            bag.SelectedMenuTitle = title || '';
        },

        async preRenderComplete() {
            this.ensureShellState();

            try {
                await utils.executeAndCompileAllActions([
                    {
                        PrimitiveAction: true,
                        ActionType: 'ShowWaitOverlay',
                        ActionData: { GreyOutBackground: true, TimedOverlay: false },
                    },
                ], null, this);

                await this.bootstrapMenus();

                // Title-bar prerender equivalents from Home5c (DesktopInfo + Customer)
                await Promise.all([
                    this.loadDesktopInfo(),
                    this.loadCustomer(),
                    this.checkAddCurrentUserToCustomer(),
                ]);

                this.rebuildMenuModel();
                this.menusReady = true;

                if (!this.backgroundServices.length) {
                    this.finishBgServices();
                }
                else {
                    this.bgServicesPending = this.backgroundServices.length;
                    // Safety: if children never finish-render, unblock chrome
                    setTimeout(() => {
                        if (!this.bgServicesSettled)
                            this.finishBgServices();
                    }, 15000);
                }

                // Visit initial static tab
                if (this.menuModel.length > 0)
                    this.selectStatic(Math.min(this.vars.ActiveStaticTab || 0, this.menuModel.length - 1));
            }
            catch (e) {
                utils.warn('CallCenterHome bootstrap failed: ' + e, e);
                this.finishBgServices();
            }
            finally {
                this.finishRenderHandler(this);
            }
        },

        async bootstrapMenus() {
            const clientType = this.System?.IsVue3 ? 'Vue3Portal' : 'VuePortal';
            const portalVersion = this.System?.PortalVersion ?? 3;
            const url = `Apps/Schema/DocumentBootstrap?clientType=${clientType}&portalVersion=${portalVersion}`;

            // Hygiene: clear sharedpanels/areas before reload (SetAllDocuments only resets menus)
            utils.document_cache.sharedpanels = {};
            utils.document_cache.areas = {};
            utils.document_cache.usercontrols = {};

            const res = await api.apiRequest({
                method: 'GET',
                url,
                doNotUseWebsocket: true,
                flatten: true,
            });

            if (res?.Success)
                utils.global.SetAllDocuments(res.Records);
            else if (Array.isArray(res))
                utils.global.SetAllDocuments(res);
            else
                utils.warn('CallCenterHome DocumentBootstrap bad response: ' + JSON.stringify(res));

            EventBus.$emit('DocumentBootstrap-MenusLoaded');
            utils.log('CallCenterHome menus loaded: ' + Object.keys(utils.document_cache.menus || {}).length);
        },

        rebuildMenuModel() {
            this.menuModel = buildCareCenterMenu(utils.document_cache.menus, (k) => this.Translate(k));
        },

        copyDebugId(value) {
            const text = value == null ? '' : String(value);
            if (!text)
                return;
            try {
                if (navigator?.clipboard?.writeText)
                    navigator.clipboard.writeText(text);
                else {
                    const ta = document.createElement('textarea');
                    ta.value = text;
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                }
            }
            catch (e) {
                utils.warn('CallCenterHome clipboard copy failed: ' + e, e);
            }
        },

        async duplicateBrowserTab() {
            // Home5c: GET Apps/DuplicateToken → OpenChildWindowWithToken (/callcenter)
            try {
                await utils.executeAndCompileAllActions([
                    {
                        PrimitiveAction: true,
                        ActionType: 'ApiRequest',
                        RunAsync: false,
                        ActionData: {
                            Method: 'GET',
                            DataType: 'RawInterpolated',
                            IncludeAccessToken: true,
                            DefaultToast: false,
                            URL: 'Apps/DuplicateToken',
                            SuccessActions: [
                                {
                                    PrimitiveAction: true,
                                    ActionType: 'OpenChildWindowWithToken',
                                    RunAsync: false,
                                    ActionData: {
                                        URL: '/callcenter',
                                        TargetWindowName: '_blank',
                                        TokenSource: 'User Provided Tokens',
                                        AccessToken: '{{Input.Data.access_token}}',
                                        RefreshToken: '{{Input.Data.refresh_token}}',
                                    },
                                },
                            ],
                            Toast: {},
                        },
                    },
                ], null, this);
            }
            catch (e) {
                utils.warn('CallCenterHome duplicateBrowserTab failed: ' + e, e);
            }
        },

        async addCurrentUserToCompany() {
            // Home5c: GET Apps/Company/AddCurrentUserToCustomer (+ success/error toasts)
            try {
                await utils.executeAndCompileAllActions([
                    {
                        PrimitiveAction: true,
                        ActionType: 'ApiRequest',
                        RunAsync: false,
                        ActionData: {
                            Method: 'GET',
                            MultiPartData: false,
                            DataType: 'RawInterpolated',
                            IncludeAccessToken: true,
                            DoNotUseWebsocket: false,
                            DefaultToast: false,
                            URL: 'Apps/Company/AddCurrentUserToCustomer',
                            Toast: {
                                SuccessToast: {
                                    Title: 'Success',
                                    Message: 'User added to customer. Refresh to take effect',
                                },
                                ErrorToast: {
                                    Title: 'Failed',
                                    Message: 'User already apart of customer or not apart of System',
                                },
                            },
                            FailureActions: [],
                        },
                    },
                ], null, this);

                await this.checkAddCurrentUserToCustomer();
            }
            catch (e) {
                utils.warn('CallCenterHome addCurrentUserToCompany failed: ' + e, e);
            }
        },

        async checkAddCurrentUserToCustomer() {
            this.canAddCurrentUserToCustomer = false;

            try {
                const response = await api.apiRequest({
                    method: 'GET',
                    url: 'Apps/Company/CheckAddCurrentUserToCustomer',
                    doNotUseWebsocket: false,
                    flatten: true,
                });
                this.canAddCurrentUserToCustomer = response === true;
            }
            catch (e) {
                utils.warn('CallCenterHome checkAddCurrentUserToCustomer failed: ' + e, e);
            }
        },

        /** Unwrap flatten / non-flatten apiRequest payloads to the Result/Data object. */
        unwrapApiPayload(res) {
            if (res == null || typeof res !== 'object')
                return res;
            if (res.Result != null && typeof res.Result === 'object')
                return res.Result;
            if (res.Data != null && typeof res.Data === 'object')
                return res.Data;
            return res;
        },

        /**
         * Home5c titleBar PrerenderActions: GET Apps/User/DesktopInfo → SetLocale / SetLanguage
         * SetLanguage only when Locale is non-empty and differs from System.LocaleKey (may reload).
         */
        async loadDesktopInfo() {
            try {
                const res = await api.apiRequest({
                    method: 'GET',
                    url: 'Apps/User/DesktopInfo',
                    doNotUseWebsocket: false,
                    flatten: true,
                });
                const userData = this.unwrapApiPayload(res);
                if (userData && typeof userData === 'object')
                    this.applyLocaleFromUserData(userData);
            }
            catch (e) {
                utils.warn('CallCenterHome loadDesktopInfo failed: ' + e, e);
            }
        },

        applyLocaleFromUserData(userData) {
            let locale = userData.Locale;
            if (locale == null || locale === '') {
                locale = 'en-US';
            }
            else {
                const localeParts = String(locale).split('-');
                let validLocale = true;
                const available = this.System?.AvailableLanguages || appSettings.AvailableLanguages || [];
                if (localeParts.length > 1) {
                    validLocale = !!available.find((l) => l.key == localeParts[0])?.locales?.find((x) => x.key == locale);
                }
                else {
                    validLocale = !!available.find((l) => l.key == localeParts[0]);
                }
                if (!validLocale)
                    locale = 'en-US';
            }

            this.vars.Locale = locale;

            // Home5c: VarByName('Locale') != "" && System.LocaleKey.toLowerCase() != Locale.toLowerCase()
            const currentKey = String(this.System?.LocaleKey || appSettings.LocaleKey || '').toLowerCase();
            if (locale !== '' && currentKey !== String(locale).toLowerCase()) {
                try {
                    careService.setSelectedLanguage(locale);
                }
                catch (e) {
                    utils.warn('CallCenterHome setSelectedLanguage failed: ' + e, e);
                }
            }
        },

        /**
         * Home5c titleBar PrerenderActions: GET Apps/Common/Customer?CallCenterHome5=true
         * → Vars.Customer, document.title, System.Currency
         */
        async loadCustomer() {
            try {
                const res = await api.apiRequest({
                    method: 'GET',
                    url: 'Apps/Common/Customer?CallCenterHome5=true',
                    doNotUseWebsocket: false,
                    flatten: true,
                });
                const customer = this.unwrapApiPayload(res);
                if (!customer || typeof customer !== 'object' || Array.isArray(customer)) {
                    utils.warn('CallCenterHome loadCustomer: unexpected response ' + JSON.stringify(res));
                    return;
                }

                this.vars.Customer = customer;

                const name = customer.Name || '';
                if (this.System?.DEBUG) {
                    // Home5c: System.careTokenService.BandID() + ' ' + Input.Data.Name
                    const band = this.System?.BandID
                        ?? this.System?.careTokenService?.BandID?.()
                        ?? '';
                    document.title = `${band} ${name}`.trim();
                }
                else {
                    document.title = name;
                }

                if (customer.DefaultCurrency != null && customer.DefaultCurrency !== '')
                    appSettings.Currency = customer.DefaultCurrency;
            }
            catch (e) {
                utils.warn('CallCenterHome loadCustomer failed: ' + e, e);
            }
        },

        finishBgServices() {
            if (this.bgServicesSettled)
                return;
            this.bgServicesSettled = true;
            this.vars.bgServicesReady = true;
            this.refreshHsmDebugVisible();
            utils.executeAndCompileAllActions([
                {
                    PrimitiveAction: true,
                    ActionType: 'HideWaitOverlay',
                    ActionData: {},
                },
            ], null, this).catch(() => {});
        },

        refreshHsmDebugVisible() {
            this.hsmDebugVisible = utils.hsmlist.hsms.some((e) => e.hsm?.visible);
        },

        onBgServiceFinished(ref) {
            this.finishRenderHandler(ref);
            if (this.bgServicesSettled)
                return;
            this.bgServicesPending = Math.max(0, this.bgServicesPending - 1);
            if (this.bgServicesPending <= 0)
                this.finishBgServices();
        },

        svcKey(svc, si) {
            return svc?._id || svc?.Name || si;
        },

        bgServiceControlData(/* svc */) {
            // Use {# SourceData() #} so nested {{...}} expressions are NOT deep-compiled here
            // (child controls compile their own ControlData in the correct context).
            return {
                Content: '{# SourceData()?.MenuItemData #}',
                IsBase: false,
                Name: 'bgServices',
            };
        },

        wrapControlContent(ctrl) {
            // Area controls are full DynamicControl nodes; Content via SourceData avoids deep eval.
            // Home5: DCC is only Content + HorizontalStack — Fixed 0 parking is on the host
            // (.cch-alert-area), not on each DCC SizeOptions.
            if (ctrl?.ControlData && ctrl?.ControlType) {
                return {
                    Content: '{# SourceData()?.ControlData #}',
                    IsBase: false,
                    ContainerType: 'HorizontalStack',
                    Name: ctrl.Name || ctrl.ControlData?.Name || 'areaCtrl',
                };
            }
            return {
                Content: '{# SourceData() #}',
                IsBase: false,
                ContainerType: 'HorizontalStack',
                Name: 'areaCtrl',
            };
        },

        wrapAlertAreaContent(ctrl) {
            return this.wrapControlContent(ctrl);
        },

        wrapTitleRightContent(ctrl) {
            return this.wrapControlContent(ctrl);
        },

        // —— Navigation API ——

        navEntriesEqual(a, b) {
            if (!a || !b || a.type !== b.type)
                return false;
            if (a.type === 'dynamic')
                return a.tabId != null && a.tabId === b.tabId;
            return a.staticIndex === b.staticIndex
                && String(a.menuSelectedId ?? '') === String(b.menuSelectedId ?? '');
        },

        isNavEntryValid(entry) {
            if (!entry)
                return false;
            if (entry.type === 'dynamic') {
                if (entry.tabId == null)
                    return false;
                return this.vars.DynamicTabArray.some((t) => t.ID === entry.tabId);
            }
            if (entry.type === 'static') {
                const i = entry.staticIndex;
                return typeof i === 'number' && i >= 0 && i < this.menuModel.length;
            }
            return false;
        },

        pushNavHistory(entry) {
            if (this._suppressNavHistory || !entry)
                return;

            // Menu/static locations cannot be "closed", so they never need prior
            // history behind them. Reset the stack and keep only this root.
            if (entry.type === 'static') {
                this.navHistory = [entry];
                return;
            }

            const last = this.navHistory[this.navHistory.length - 1];
            if (this.navEntriesEqual(last, entry))
                return;
            this.navHistory.push(entry);
            if (this.navHistory.length > NAV_HISTORY_MAX)
                this.navHistory.splice(0, this.navHistory.length - NAV_HISTORY_MAX);
        },

        pushStaticNav(staticIndex, menuSelectedId) {
            let menuId = menuSelectedId;
            if (menuId === undefined) {
                const fromList = this.menuListRefs[staticIndex]?.SelectedId;
                const fromActive = this.activeMenuContentByStatic[staticIndex]?.key;
                menuId = fromList != null && fromList !== ''
                    ? fromList
                    : (fromActive != null && fromActive !== '' ? fromActive : null);
            }
            this.pushNavHistory({
                type: 'static',
                staticIndex,
                menuSelectedId: menuId != null && menuId !== '' ? menuId : null,
            });
        },

        pushDynamicNav(tabId) {
            if (tabId == null || tabId === '')
                return;
            this.pushNavHistory({ type: 'dynamic', tabId });
        },

        pruneNavHistoryForTab(tabId) {
            if (tabId == null)
                return;
            this.navHistory = this.navHistory.filter(
                (e) => !(e.type === 'dynamic' && e.tabId === tabId),
            );
        },

        findPreviousNavEntry() {
            for (let i = this.navHistory.length - 1; i >= 0; i--) {
                if (this.isNavEntryValid(this.navHistory[i]))
                    return this.navHistory[i];
            }
            return null;
        },

        async restoreNavEntry(entry, options = {}) {
            if (!entry || !this.isNavEntryValid(entry))
                return;

            this._suppressNavHistory = true;
            try {
                if (entry.type === 'dynamic') {
                    const idx = this.vars.DynamicTabArray.findIndex((t) => t.ID === entry.tabId);
                    if (idx >= 0)
                        void this.selectDynamic(idx);
                    return;
                }

                this.selectStatic(entry.staticIndex, options);
                if (entry.menuSelectedId != null && entry.menuSelectedId !== '') {
                    const menuList = await this.waitForMenuList(entry.staticIndex);
                    if (menuList?.SelectItem)
                        menuList.SelectItem(entry.menuSelectedId);
                }
            }
            finally {
                await nextTick();
                this._suppressNavHistory = false;
            }
        },

        // Vue2 CallCenterHome DynamicTabContent runs OnActivate/OnDeActivate for CareCenter
        // static menus and for open document tabs. Do not await — OpenChildWindow needs the
        // user-gesture stack for window.open.
        runStaticMenuDeactivate(index) {
            if (index < 0 || index >= this.menuModel.length)
                return;
            const prevData = this.menuModel[index]?.Data;
            const prevMid = prevData?.MenuItemData;
            const deacts = prevMid?.OnDeactivateActions || prevMid?.OnDeActivateActions;
            if (deacts?.length)
                utils.executeAndCompileAllActions(deacts, { Data: prevData }, this);
        },

        runStaticMenuActivate(index) {
            const item = this.menuModel[index];
            const mid = item?.Data?.MenuItemData;
            if (mid?.OnActivateActions?.length)
                utils.executeAndCompileAllActions(mid.OnActivateActions, { Data: item.Data }, this);
        },

        runDynamicTabDeactivate(tab) {
            if (!tab)
                return;
            const deacts = tab.OnDeactivateActions || tab.OnDeActivateActions;
            if (deacts?.length)
                utils.executeAndCompileAllActions(deacts, {}, tab.context || this);
        },

        runDynamicTabActivate(tab) {
            if (!tab?.OnActivateActions?.length)
                return;
            utils.executeAndCompileAllActions(tab.OnActivateActions, {}, tab.context || this);
        },

        /** Vue2 DynamicTabContent: first-render activate only after finished-render. */
        onDynamicTabFinishedRender(tab, component) {
            this.finishRenderHandler(component);
            const id = tab?.ID;
            if (id == null || id === '')
                return;
            if (this.renderedDynamicTabs.has(id))
                return;

            this.renderedDynamicTabs.add(id);

            const activeIdx = this.vars.ActiveDynamicTab;
            const active = activeIdx >= 0 ? this.vars.DynamicTabArray[activeIdx] : null;
            if (active?.ID !== id)
                return;

            // Match Vue2 updateTabRenderStatus: activate on next tick after render complete.
            nextTick(() => {
                if (this.vars.DynamicTabArray[this.vars.ActiveDynamicTab]?.ID === id)
                    this.runDynamicTabActivate(tab);
            });
        },

        selectStatic(index, options = {}) {
            const i = typeof index === 'string' ? parseInt(index, 10) : index;
            if (isNaN(i) || i < 0 || i >= this.menuModel.length)
                return;

            const item = this.menuModel[i];
            if (item?.kind === 'PopupMenu' || item?.kind === 'ActionsOnly')
                return;

            const prevIndex = this.vars.ActiveStaticTab;
            const prevDyn = this.vars.ActiveDynamicTab;
            const leavingStatic = prevDyn < 0;
            const sameStaticVisible = leavingStatic && prevIndex === i;
            const firstVisit = !this.visitedStaticTabs.includes(i);
            const switching = !sameStaticVisible || firstVisit || options.reactivate === true;

            if (!leavingStatic)
                this.runDynamicTabDeactivate(this.vars.DynamicTabArray[prevDyn]);
            else if (switching && prevIndex !== i)
                this.runStaticMenuDeactivate(prevIndex);

            this.vars.ActiveStaticTab = i;
            this.vars.ActiveDynamicTab = -1;
            this.markVisitedStatic(i);
            this.pushStaticNav(i);

            if (switching)
                this.runStaticMenuActivate(i);
        },

        async selectDynamic(index) {
            const i = typeof index === 'string' ? parseInt(index, 10) : index;
            const tabs = this.vars.DynamicTabArray;
            if (isNaN(i) || i < 0 || i >= tabs.length)
                return;

            const prevDyn = this.vars.ActiveDynamicTab;
            const onStatic = prevDyn < 0;
            if (!onStatic && prevDyn === i)
                return;

            // After closing the active dynamic tab we may re-select another without having
            // returned to static — skip the static deactivate in that handoff.
            const skipStaticDeactivate = this._skipStaticDeactivateOnDynamicSelect === true;
            this._skipStaticDeactivateOnDynamicSelect = false;

            if (onStatic) {
                if (!skipStaticDeactivate)
                    this.runStaticMenuDeactivate(this.vars.ActiveStaticTab);
            }
            else {
                this.runDynamicTabDeactivate(tabs[prevDyn]);
            }

            // Vue2 DynamicTabContent.doChangeSelectedTab: yield between deactivate and selection.
            await nextTick();

            this.vars.ActiveDynamicTab = i;
            this.pushDynamicNav(tabs[i]?.ID);

            const tab = tabs[i];
            const tabId = tab?.ID;
            const alreadyRendered = tabId != null && tabId !== '' && this.renderedDynamicTabs.has(tabId);
            // No ControlData ⇒ nothing will emit finished-render; activate after tick like a re-visit.
            const canActivateNow = alreadyRendered || !tab?.ControlData;

            if (canActivateNow) {
                await nextTick();
                if (this.vars.ActiveDynamicTab === i)
                    this.runDynamicTabActivate(this.vars.DynamicTabArray[i]);
            }
            // else: onDynamicTabFinishedRender runs OnActivate after first mount
        },

        openTab(payload) {
            const tabs = this.vars.DynamicTabArray;
            const existing = payload?.TabID != null
                ? tabs.findIndex((t) => t.ID == payload.TabID)
                : -1;

            if (existing >= 0) {
                void this.selectDynamic(existing);
                return;
            }

            const tab = normalizeOpenTab(payload, () => utils.generateUUID());
            // Preserve caller TabID if provided
            if (payload?.TabID)
                tab.ID = payload.TabID;
            tabs.push(tab);
            void this.selectDynamic(tabs.length - 1);
        },

        closeDynamic(index) {
            const tabs = this.vars.DynamicTabArray;
            const i = typeof index === 'string' ? parseInt(index, 10) : index;
            if (isNaN(i) || i < 0 || i >= tabs.length)
                return;

            const closedTab = tabs[i];
            const closedId = closedTab?.ID;
            const closingActive = this.vars.ActiveDynamicTab === i;

            if (closingActive)
                this.runDynamicTabDeactivate(closedTab);

            tabs.splice(i, 1);
            this.pruneNavHistoryForTab(closedId);
            if (closedId != null && closedId !== '')
                this.renderedDynamicTabs.delete(closedId);

            if (!closingActive) {
                // Background tab closed — keep the same logical active tab.
                if (this.vars.ActiveDynamicTab > i)
                    this.vars.ActiveDynamicTab--;
                return;
            }

            // Active tab closed (already deactivated). Clear before restore so indices are valid.
            this.vars.ActiveDynamicTab = -1;

            const prev = this.findPreviousNavEntry();
            if (prev?.type === 'dynamic') {
                this._skipStaticDeactivateOnDynamicSelect = true;
                void this.restoreNavEntry(prev);
                return;
            }
            if (prev) {
                // Returning to static — re-run OnActivate (static was deactivated when we left).
                void this.restoreNavEntry(prev, { reactivate: true });
                return;
            }

            if (tabs.length === 0) {
                this.selectStatic(this.vars.ActiveStaticTab, { reactivate: true });
                return;
            }

            // Safety net when history is empty but tabs remain
            this._skipStaticDeactivateOnDynamicSelect = true;
            void this.selectDynamic(Math.min(i, tabs.length - 1));
        },

        renameDynamic(index, title) {
            const tabs = this.vars.DynamicTabArray;
            const i = typeof index === 'string' ? parseInt(index, 10) : index;
            if (isNaN(i) || i < 0 || i >= tabs.length)
                return;
            const { Title, Tooltip } = parseTabTitle(title);
            tabs[i].Title = Title;
            tabs[i].Tooltip = Tooltip;
        },

        selectMenuByName(name) {
            if (!name)
                return;
            const staticName = String(name).split('.')[0];
            const index = this.menuModel.findIndex((m) => m.Name === staticName);
            if (index >= 0)
                this.selectStatic(index);
            // Submenu selection is handled by DynamicMenuList via Action-SelectMenuItem-Menu
        },

        async selectMenuByPath(path) {
            if (!path)
                return;
            const parts = String(path).split('/');
            const idx = this.menuModel.findIndex((m) => m.Data?.MenuItemData?.Title === parts[0]);
            if (idx < 0)
                return;

            this.selectStatic(idx);

            if (parts.length < 2)
                return;

            // Wait until DynamicMenuList for this static tab has MenuData
            const menuList = await this.waitForMenuList(idx);
            if (!menuList)
                return;

            let currentMenu = menuList.MenuData;
            for (let x = 1; x < parts.length; x++) {
                const part = parts[x];
                const item = (currentMenu || []).find((m) => m.item_data?.MenuItemData?.Title === part);
                if (!item)
                    return;
                if (x !== parts.length - 1) {
                    if (item.children?.length)
                        currentMenu = item.children;
                    else
                        return;
                }
                else {
                    menuList.SelectItem(item.id);
                }
            }
        },

        waitForMenuList(staticIndex, timeoutMs = 5000) {
            return new Promise((resolve) => {
                const start = Date.now();
                const tick = () => {
                    const ml = this.menuListRefs[staticIndex];
                    if (ml?.MenuData)
                        return resolve(ml);
                    if (Date.now() - start > timeoutMs)
                        return resolve(null);
                    requestAnimationFrame(tick);
                };
                tick();
            });
        },

        markVisitedStatic(index) {
            if (!this.visitedStaticTabs.includes(index))
                this.visitedStaticTabs = [...this.visitedStaticTabs, index];
        },

        // —— Nav handlers ——

        onNavSelect(id) {
            if (id === '__collapse__')
                return;
            this.selectStatic(id);
        },

        onNavMenuAction({ menuItem, handled }) {
            if (handled)
                return;
            const actionData = menuItem?._navChild?.Data?.MenuItemData?.Actions
                ?? menuItem?.Data?.MenuItemData?.Actions;
            if (actionData)
                utils.executeAndCompileAllActions(actionData, null, this);
        },

        onNavAction({ item, handled }) {
            if (handled)
                return;
            if (item?._navAction) {
                item._navAction();
                return;
            }
            if (item?._navTab?.Actions)
                utils.executeAndCompileAllActions(item._navTab.Actions, null, this);
        },

        // —— Static tab helpers ——

        staticMenuItem(idx) {
            return this.menuModel[idx]?.Data;
        },

        childMenuType(idx) {
            return this.menuModel[idx]?.type || this.menuModel[idx]?.Data?.MenuItemData?.ChildMenuType;
        },

        isNoneStaticTab(idx) {
            const item = this.menuModel[idx];
            if (!item)
                return false;
            const childType = item.type || item.Data?.MenuItemData?.ChildMenuType;
            const menuItemType = item.kind || item.Data?.MenuItemType;
            return childType === 'None' && menuItemType === 'StaticTab';
        },

        isMenuStaticTab(idx) {
            const t = this.childMenuType(idx);
            return t === 'MenuName' || t === 'URL';
        },

        /** MenuName/URL tabs need a hidden host so parent ControlData PostrenderActions run (Vue2 staticTabContent). */
        needsHiddenStaticTabHost(idx) {
            return this.isMenuStaticTab(idx);
        },

        staticNoneControlData(idx) {
            return this.staticTabContentControlData(idx);
        },

        staticTabHostControlData(idx) {
            return this.staticTabContentControlData(idx, { visible: false });
        },

        staticTabContentControlData(idx, { visible = true } = {}) {
            const data = {
                Content: '{# SourceData()?.MenuItemData?.ControlData #}',
                IsBase: true,
                Name: 'staticTabContent_' + idx,
            };
            if (!visible)
                data.VisibleExpression = 'false';
            return data;
        },

        menuListControlData(idx) {
            const item = this.menuModel[idx];
            const mid = item?.Data?.MenuItemData || {};
            const isUrl = mid.ChildMenuType === 'URL';
            const title = mid.ChildMenuDisplayName || mid.Title || '';

            if (isUrl) {
                return {
                    DarkTheme: false,
                    SourceType: 'Url',
                    SourceUrl: mid.ChildMenuURL || '',
                    SourceAsMenuItems: true,
                    MenuTitle: title,
                    MenuTitleBackgroundColor: 'var(--v-navHeader-base)',
                    Name: 'menuList_' + idx,
                    SizeOptions: {
                        Width: { Mode: 'Fixed', Units: 'Pixels', Value: 310 },
                        Height: { Mode: 'Fill' },
                    },
                    StyleHints: {
                        CustomStyle: 'margin-left: -1px;margin-top: -1px;',
                    },
                };
            }

            const menuName = mid.ChildMenuName || '';
            return {
                DarkTheme: false,
                SourceType: 'Raw',
                SourceRaw: `{# DocumentCache.menus[${JSON.stringify(menuName)}] #}`,
                SourceAsMenuItems: true,
                MenuTitle: title,
                MenuTitleBackgroundColor: 'var(--v-navHeader-base)',
                Name: 'menuList_' + idx,
                SizeOptions: {
                    Width: { Mode: 'Fixed', Units: 'Pixels', Value: 310 },
                    Height: { Mode: 'Fill' },
                },
                StyleHints: {
                    CustomStyle: 'margin-left: -1px;margin-top: -1px;',
                },
            };
        },

        setMenuListRef(idx, el) {
            if (el)
                this.menuListRefs[idx] = el;
            else
                delete this.menuListRefs[idx];
        },

        onMenuListReady(idx, component) {
            this.finishRenderHandler(component);
            const vm = component;
            if (!vm || this.menuListWatchers[idx])
                return;

            this.menuListRefs[idx] = vm;
            this.menuListWatchers[idx] = this.$watch(
                () => vm.SelectedId,
                (id) => this.onMenuSelectionChanged(idx, id, vm),
                { immediate: true },
            );
        },

        onMenuSelectionChanged(staticIndex, selectedId, menuListVm) {
            if (selectedId == null || selectedId === '') {
                this.writeSelectedMenuFields('', '');
                return;
            }

            const contentMap = menuListVm?.MenuContent;
            const menuItem = Array.isArray(contentMap)
                ? contentMap.find((c) => c.id == selectedId)
                : contentMap?.[selectedId];

            const mid = menuItem?.item_data?.MenuItemData;
            this.writeSelectedMenuFields(mid?.Name || '', mid?.Title || '');

            const resolved = resolveMenuContent(menuItem, utils.document_cache.sharedpanels);
            if (!resolved) {
                this.activeMenuContentByStatic = {
                    ...this.activeMenuContentByStatic,
                    [staticIndex]: null,
                };
                return;
            }

            if (resolved.kind === 'missing') {
                if (!this.missingSharedLogged[resolved.key]) {
                    this.missingSharedLogged[resolved.key] = true;
                    utils.warn(`CallCenterHome: SharedPanel "${resolved.key}" not found in document_cache.sharedpanels`);
                }
                this.activeMenuContentByStatic = {
                    ...this.activeMenuContentByStatic,
                    [staticIndex]: resolved,
                };
                return;
            }

            if (resolved.kind === 'shared') {
                const list = this.visitedSharedByStatic[staticIndex] || [];
                if (!list.includes(resolved.key)) {
                    this.visitedSharedByStatic = {
                        ...this.visitedSharedByStatic,
                        [staticIndex]: [...list, resolved.key],
                    };
                }
            }
            else if (resolved.kind === 'item') {
                const list = this.visitedItemsByStatic[staticIndex] || [];
                if (!list.includes(resolved.key)) {
                    this.visitedItemsByStatic = {
                        ...this.visitedItemsByStatic,
                        [staticIndex]: [...list, resolved.key],
                    };
                }
            }

            this.activeMenuContentByStatic = {
                ...this.activeMenuContentByStatic,
                [staticIndex]: resolved,
            };

            // Only record menu visits while viewing the static layer for this nav item
            // (avoid pushing when a background menu list hydrates while a dynamic tab is active).
            if (this.vars.ActiveDynamicTab < 0 && this.vars.ActiveStaticTab === staticIndex)
                this.pushStaticNav(staticIndex, selectedId);
        },

        visitedItemKeys(idx) {
            return this.visitedItemsByStatic[idx] || [];
        },

        visitedSharedKeys(idx) {
            return this.visitedSharedByStatic[idx] || [];
        },

        isActiveMenuContent(idx, kind, key) {
            const active = this.activeMenuContentByStatic[idx];
            return active && active.kind === kind && String(active.key) === String(key);
        },

        missingSharedMessage(idx) {
            const active = this.activeMenuContentByStatic[idx];
            return active?.kind === 'missing' ? active.message : null;
        },

        menuItemEntry(staticIndex, itemKey) {
            const ml = this.menuListRefs[staticIndex];
            const contentMap = ml?.MenuContent;
            if (Array.isArray(contentMap))
                return contentMap.find((c) => String(c.id) === String(itemKey)) || null;
            return contentMap?.[itemKey] || null;
        },

        sharedPanelDoc(panelName) {
            const doc = utils.document_cache.sharedpanels?.[panelName] || null;
            // Attach stable Controls: [Data] host so leaf roots (SandboxedApp) mount.
            if (doc)
                ensureSharedPanelHostControlData(doc);
            return doc;
        },

        itemPanelControlData(staticIndex, itemKey) {
            return {
                Content: '{# SourceData()?.item_data?.MenuItemData?.ControlData #}',
                IsBase: true,
                Name: 'menuItemContent_' + staticIndex + '_' + itemKey,
            };
        },

        sharedPanelControlData(panelName) {
            return {
                // Full Data node (not Data.ControlData) — see ensureSharedPanelHostControlData
                Content: '{# SourceData()?.$cchSharedHost #}',
                IsBase: true,
                Name: 'sharedPanel_' + panelName,
            };
        },

        dynamicTabControlData(tab, ti) {
            return {
                Content: '{# SourceData()?.ControlData #}',
                ActionData: '{# SourceData()?.ActionData #}',
                IsBase: true,
                Name: 'dynTab_' + (tab?.ID || ti),
            };
        },

        // —— EventBus ——

        async onActionTab(action) {
            try {
                if (!action?.ActionData) {
                    action?.FinishFunc?.(true);
                    return;
                }

                if (action.actiondatafunc) {
                    if (!action.context.$attrs)
                        action.context.$attrs = {};
                    action.context.$attrs.paramData = utils.evaluateObject(action.actiondatafunc, action.context);
                }

                const controlData = JSON.parse(JSON.stringify(action.ActionData.ControlData || {}));
                if (action.varsobj) {
                    controlData.Vars = utils.evaluateObject(action.varsobj, action.context);
                    controlData.$nointerp = true;
                }

                const tabID = action.tabID ? utils.evaluate(action.tabID, action.context) : '';
                const tabTitle = action.tabTitle ? utils.evaluate(action.tabTitle, action.context) : '';

                this.openTab({
                    TabID: tabID,
                    TabTitle: tabTitle,
                    ActionData: action.context?.$attrs?.paramData || {},
                    ControlData: controlData,
                    OnActivateActions: action.ActionData.OnActivateActions || [],
                    OnDeactivateActions: action.ActionData.OnDeactivateActions || [],
                    context: action.context,
                });

                try { await utils.complete(action); } catch (e) { /* ignore */ }
                action.FinishFunc?.(true);
            }
            catch (e) {
                utils.warn('CallCenterHome onActionTab failed: ' + e, e);
                action?.FinishFunc?.(false);
            }
        },

        async onActionRenameTab(action) {
            try {
                if (action.tabtitle) {
                    const title = utils.evaluate(action.tabtitle, action.context);
                    const index = this.vars.ActiveDynamicTab;
                    this.renameDynamic(index, title);
                }
                try { await utils.complete(action); } catch (e) { /* ignore */ }
                action.FinishFunc?.(true);
            }
            catch (e) {
                action?.FinishFunc?.(false);
            }
        },

        async onActionClose(action) {
            try {
                this.closeDynamic(this.vars.ActiveDynamicTab);
                try { await utils.complete(action); } catch (e) { /* ignore */ }
                action.FinishFunc?.(true);
            }
            catch (e) {
                action?.FinishFunc?.(false);
            }
        },

        async onActionSelectMenuItem(action) {
            try {
                const name = action.menuname
                    ? utils.evaluate(action.menuname, action.context)
                    : action.ActionData?.MenuItemName;

                this.selectMenuByName(name);

                // Submenu: "StaticName.ItemName" — select on the DynamicMenuList once it is ready
                const parts = name != null ? String(name).split('.') : [];
                if (parts.length > 1) {
                    const staticIndex = this.menuModel.findIndex((m) => m.Name === parts[0]);
                    if (staticIndex >= 0) {
                        const menuList = await this.waitForMenuList(staticIndex);
                        if (menuList?.MenuData) {
                            const item = menuList.MenuData.find((m) => m.item_data?.MenuItemData?.Name === parts[1]);
                            if (item)
                                menuList.SelectItem(item.id);
                        }
                    }
                }

                try { await utils.success(action); } catch (e) { /* ignore */ }
                try { await utils.complete(action); } catch (e) { /* ignore */ }
                action.FinishFunc?.(true);
            }
            catch (e) {
                try { await utils.failure(action, { Data: e }); } catch (err) { /* ignore */ }
                action?.FinishFunc?.(false);
            }
        },

        tryOpenEditorFromQuery() {
            if (this.openedEditorFromQuery)
                return;
            this.openedEditorFromQuery = true;

            const type = this.Query('type');
            const name = this.Query('name');
            const id = this.Query('id');
            if (!type || !(name || id))
                return;

            this.onOpenDocumentEditor({
                Name: name,
                Type: type,
                Id: id,
                Title: this.Query('title'),
                Data: this.Query('data'),
            });
        },

        onOpenDocumentEditor(args) {
            const name = args?.Name;
            const type = args?.Type;
            const folder = args?.Folder || '';
            const id = args?.Id;
            const docUrl = args?.URL;
            const hasName = !!(name != null && String(name) !== '');

            let url;
            let tabTitle = name || type;
            let tabID = `VuePortal_Edit_${name || type}_${id}`;
            let vars = { Name: name, Type: type };
            let hostControlURL = 'public/v1/Vue/EmbeddedEditor_v3';
            let hostControlName = 'Unnamed';
            let hostControlData = null;

            // Home5c openEditorListener: !Name && (Controls|Actions) → NewEmptyDocument
            if (!hasName && (type === 'Controls' || type === 'Actions')) {
                url = `Apps/SchemaBootstrap/NewEmptyDocument/${type}/${folder}`;
                tabTitle = `New ${type}`;
                tabID = '';
                vars = { Folder: folder, Type: type };
            }
            // Home5c: Type == NewSchema
            else if (type === 'NewSchema') {
                url = 'Apps/SchemaBootstrap/NewEmptyDocument/Schema/';
                tabTitle = 'NewSchema';
                tabID = '';
                vars = {};
            }
            // Home5: GetDocumentBy == 'URL' && URL
            else if (args?.GetDocumentBy === 'URL' && docUrl) {
                url = docUrl;
                tabTitle = docUrl;
                tabID = `VuePortal_Edit_${docUrl}`;
                vars = { URL: docUrl };
            }
            // Home5: Id && Type == Cassandra — Vars is the full Input
            else if (type === 'Cassandra' && id) {
                url = `Apps/Raw/CassandraKeyspace/${id}`;
                tabTitle = name || String(id);
                tabID = `VuePortal_Edit_${id}`;
                vars = { ...args };
            }
            // Home5: Name && Type == Page — open the user control with PrepopData (not EmbeddedEditor)
            else if (type === 'Page' && hasName) {
                tabTitle = args?.Title || name;
                tabID = '';
                vars = { Name: name, Data: args?.Data };
                hostControlURL = name;
                hostControlName = 'PrepopUserControl';
                hostControlData = { PrepopData: args?.Data };
            }
            else if (type == 'MenuItem' || type == 'UserDefinedArea') {
                if (args?.Public)
                    url = `Apps/Raw/UI/${type}/public/${id}`;
                else
                    url = `Apps/Raw/UI/${type}/${id}`;
                vars = { ...args };
            }
            else {
                url = `Apps/UIWhole/${type}/${name}`;
            }

            const controlData = {
                BreadCrumb: { Action: 'Replace' },
                DisplayFeedback: 'Default',
                SizeOptions: {
                    Width: { Mode: 'Fill' },
                    Height: { Mode: 'Fill' },
                },
                Name: 'tabcontainer',
                Vars: vars,
                $nointerp: true,
                Controls: [
                    {
                        PrimitiveControl: false,
                        ControlURL: hostControlURL,
                        CacheControl: true,
                        Name: hostControlName,
                        ControlData: hostControlData || { ControlURL: url },
                    },
                ],
            };

            this.openTab({
                TabID: tabID,
                TabTitle: tabTitle,
                ControlData: controlData,
            });
        },
    },
};
</script>

<style scoped>
.c-CallCenterHome {
    position: relative;
    background: transparent;
}

.cch-bg-services {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
    pointer-events: none;
}

.cch-bg-services--debug {
    position: relative;
    width: auto;
    height: auto;
    overflow: visible;
    pointer-events: auto;
}

.cch-titlebar {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
    height: 60px;
    min-height: 60px;
    padding: 0 8px;
    gap: 8px;
    anchor-name: --main-header;
}

.cch-busy {
    position: absolute;
    z-index: 10;
}

.cch-logo {
    width: 40px;
    height: 40px;
    object-fit: contain;
    margin-left: 8px;
}

.cch-customer {
    font-size: x-large;
    padding-left: 8px;
    padding-top: 4px;
    /* Reserve natural width up to 66%; never shrink for tabs (avoids arrow flicker). */
    flex: 0 0 auto;
    min-width: 0;
    max-width: 66%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cch-debug {
    flex-shrink: 0;
}

.cch-debug-menu-btn {
    flex-shrink: 0;
    margin-left: 2px;
}

.cch-tabs-grow {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: flex-end;
    height: 100%;
    overflow: hidden;
}

.cch-dyn-tabs {
    max-width: 100%;
    background: transparent;
    align-self: flex-end;
    height: auto;
}

/* Quasar defaults stretch every tab to the row height — opt out so only active can grow. */
.cch-dyn-tabs :deep(.q-tabs__content),
.cch-dyn-tabs :deep(.q-tabs__list) {
    align-items: flex-end !important;
    height: auto !important;
}

.cch-dyn-tabs :deep(.q-tabs__indicator) {
    height: 2px;
}

.cch-dyn-tabs :deep(.q-tab__content) {
    flex-wrap: nowrap;
    height: auto !important;
}

/*
 * Title-bar dynamic tabs share the left nav background; active/inactive are
 * distinguished by text opacity, height and the active tab's inset edge.
 * Pin inactive height; only the active tab is taller (bottom-aligned).
 */
.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab) {
    color: rgba(255, 255, 255, 0.68) !important;
    background-color: var(--v-navigation-base) !important;
    height: 36px !important;
    min-height: 36px !important;
    max-height: 36px !important;
    padding: 0 12px;
    margin: 0;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    opacity: 1;
    align-self: flex-end;
}

.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab .q-tab__label),
.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab .cch-dyn-tab-label),
.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab .q-icon) {
    color: inherit !important;
}

.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab:hover) {
    color: rgba(255, 255, 255, 0.92) !important;
}

.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab.q-tab--active),
.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab.q-router-link--active) {
    color: #ffffff !important;
    background-color: var(--v-navigation-base) !important;
    height: 44px !important;
    min-height: 44px !important;
    max-height: 44px !important;
    /* Subtle top/side edge so the selected tab reads against a dark title bar. */
    box-shadow:
        inset 0 1px 0 0 rgba(255, 255, 255, 0.32),
        inset 1px 0 0 0 rgba(255, 255, 255, 0.2),
        inset -1px 0 0 0 rgba(255, 255, 255, 0.2);
}

.cch-dyn-tab-inner {
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
    white-space: nowrap;
    max-width: 220px;
    gap: 6px;
}

.cch-dyn-tab-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.cch-dyn-tab-close {
    flex: 0 0 auto;
    color: rgba(255, 255, 255, 0.55) !important;
}

.cch-dyn-tabs :deep(.q-tab.cch-dyn-tab.q-tab--active .cch-dyn-tab-close) {
    color: #ffffff !important;
}

/* Home5 alertArea host: Fixed 0×0 so idle notify shells don't reserve title-bar space.
   Content parks via .mainBodyAlertNotification { position:absolute; right:-100% }.
   Do not position this host — a 0-width containing block breaks right:-100% parking
   and makes top:72px drop the chrome into the main body. Parking resolves against
   .c-CallCenterHome (position:relative). */
.cch-alert-area {
    width: 0;
    height: 0;
    flex: 0 0 auto;
    overflow: visible;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
}

/* Neutralize DCC's default flexGrow:1 inside the park host (Home5 sized the
   HorizontalLayout host to 0 instead — we keep DynamicControlContainer unchanged). */
.cch-alert-area :deep(.c-DynamicControlContainer) {
    flex-grow: 0 !important;
    flex-shrink: 0 !important;
    width: 0 !important;
    height: 0 !important;
    min-width: 0 !important;
    min-height: 0 !important;
    overflow: visible !important;
}

.cch-alert-area :deep(.c-DynamicControlContainer > *) {
    overflow: visible !important;
}

.cch-title-right {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-shrink: 0;
}

.cch-main {
    display: flex;
    flex-direction: row;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.cch-nav {
    height: 100%;
}

/* While a dynamic tab is showing, keep last static marked via IconDrawer --active,
   but restyle it as a faded "last visited" hint instead of full selection. */
.cch-nav--dynamic-active :deep(.icon-drawer__item--active) {
    background-color: rgb(from var(--q-primary-inverse, #fff) r g b / 0.10);
    color: inherit;
    opacity: 0.55;
}

.cch-nav--dynamic-active :deep(.icon-drawer__item--active:hover) {
    background-color: rgb(from var(--q-primary-inverse, #fff) r g b / 0.16);
    opacity: 0.75;
}

.cch-content {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: var(--v-pageBackground-base);
}

.cch-panel {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    /* Keep overflow on fill hosts so Height:Fill / flexGrow children get a definite height (grids). */
    overflow: hidden;
    min-height: 0;
}

.cch-menu-layout {
    display: flex;
    flex-direction: row;
    flex: 1 1 auto;
    align-items: stretch;
    min-height: 0;
    min-width: 0;
    height: 100%;
    width: 100%;
    overflow: hidden;
}

.cch-menu-list-host {
    flex: 0 0 auto;
    align-self: stretch;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.cch-menu-list-host > :deep(*) {
    height: 100%;
    min-height: 0;
    align-self: stretch;
}

.cch-menu-content {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    background-color: var(--v-pageBackground-base);
}

/* Absolute layer matching Home5c DynamicTabContent PersistViewSpace fill behavior */
.cch-fill-host {
    position: absolute;
    inset: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    padding-top: 8px;
    padding-inline: 6px;
    overflow: auto;
}

.cch-fill-host > :deep(*) {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
}

.cch-missing-shared {
    position: absolute;
    inset: 0;
}
</style>
